import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { fmtINR, today } from "@/lib/format";
import { toast } from "sonner";
import { Building2, Layers, UserPlus, Link2Off, CheckCircle2, XCircle, Clock, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const fyStart = () => { const t = new Date(); const y = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1; return `${y - 1}-04-01`; };

/** Simple hierarchical org-tree renderer with drag-to-reparent.
 *  - Drag any node onto another node to make the drop-target the new parent.
 *  - Confirm agreed ownership % on drop.
 *  - Cycle detection is server-side (rejects "make X child of its own descendant").
 *  - Click a node to include/exclude it from the current consolidation run. */
function GroupTree({ entities, selected, onToggle, onReparent }) {
  const [dragId, setDragId] = React.useState(null);
  const [dropTargetId, setDropTargetId] = React.useState(null);

  const handleDragStart = (id, e) => { setDragId(id); e.dataTransfer.effectAllowed = "move"; };
  const handleDragOver = (id, e) => { e.preventDefault(); if (id !== dragId) setDropTargetId(id); };
  const handleDrop = (targetId, e) => {
    e.preventDefault();
    setDropTargetId(null);
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const pct = window.prompt(`Set ownership % of the dragged entity under its new parent (1–100):`, "100");
    if (!pct) { setDragId(null); return; }
    onReparent?.(dragId, targetId, Number(pct));
    setDragId(null);
  };

  // Group children by parent for multi-level layout
  const byParent = {};
  entities.forEach((e) => {
    const p = e.parent_org_id || "__root";
    (byParent[p] = byParent[p] || []).push(e);
  });
  const roots = entities.filter((e) => !e.parent_org_id);

  const renderNode = (e, isRoot) => {
    const isSelected = selected.includes(e.id);
    const isDrag = e.id === dragId;
    const isDropTarget = e.id === dropTargetId;
    const children = byParent[e.id] || [];
    return (
      <div key={e.id} className="flex flex-col items-center">
        <button
          draggable
          onDragStart={(ev) => handleDragStart(e.id, ev)}
          onDragOver={(ev) => handleDragOver(e.id, ev)}
          onDragLeave={() => setDropTargetId(null)}
          onDrop={(ev) => handleDrop(e.id, ev)}
          onClick={() => onToggle(e.id)}
          data-testid={`tree-node-${e.id.slice(0, 8)}`}
          className={`px-5 py-3 border-2 rounded-sm min-w-[210px] text-center transition-all cursor-grab active:cursor-grabbing
            ${isSelected ? "border-accent bg-accent/10" : "border-border bg-card"}
            ${isDrag ? "opacity-40" : ""}
            ${isDropTarget ? "ring-4 ring-accent/40 scale-105" : ""}`}>
          <div className="font-semibold text-sm">{e.name}</div>
          <div className="text-[10px] font-mono uppercase text-muted-foreground mt-1">
            {isRoot ? "PARENT" : "SUBSIDIARY"} · {e.ownership_pct}%
          </div>
        </button>
        {children.length > 0 && (
          <>
            <div className="w-px h-6 bg-border" />
            <div className="h-px bg-border" style={{ width: `${Math.min(children.length * 230, 900)}px` }} />
            <div className="flex justify-center gap-6 flex-wrap mt-0">
              {children.map((c) => (
                <div key={c.id} className="flex flex-col items-center">
                  <div className="w-px h-6 bg-border" />
                  {renderNode(c, false)}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  if (roots.length === 0) return null;
  return (
    <div className="p-8" data-testid="group-tree">
      <div className="flex justify-center gap-12 flex-wrap">
        {roots.map((r) => renderNode(r, true))}
      </div>
      <div className="text-[10px] text-center text-muted-foreground mt-4 font-mono">
        Drag any node onto another to make it a subsidiary of the drop-target.
      </div>
    </div>
  );
}

/** Invite another org to the group by GSTIN. */
function InviteDialog({ onSent }) {
  const [open, setOpen] = useState(false);
  const [gstin, setGstin] = useState("");
  const [pct, setPct] = useState(100);
  const [notes, setNotes] = useState("");
  const send = async () => {
    try {
      await api.post("/consolidation/invite", { gstin, proposed_ownership_pct: pct, notes });
      toast.success("Invite sent — the receiving org's admin needs to accept.");
      setOpen(false); setGstin(""); setPct(100); setNotes(""); onSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-sm h-9 gap-1.5" data-testid="cons-invite-btn"><UserPlus className="w-3.5 h-3.5" /> Invite entity</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Invite an entity to the group</DialogTitle></DialogHeader>
        <div className="text-xs text-muted-foreground">
          Enter the target org's GSTIN. Their admin gets a pending invitation and must accept before any data is shared. You can revoke at any time.
        </div>
        <div className="space-y-3">
          <div><Label>Target GSTIN *</Label><Input value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())} placeholder="27AAACX1234A1Z5" className="rounded-sm mt-1 font-mono" data-testid="invite-gstin" /></div>
          <div><Label>Proposed ownership % *</Label><Input type="number" min="1" max="100" value={pct} onChange={(e) => setPct(Number(e.target.value))} className="rounded-sm mt-1" data-testid="invite-pct" /></div>
          <div><Label>Notes to the admin (optional)</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="rounded-sm mt-1" data-testid="invite-notes" /></div>
        </div>
        <Button onClick={send} className="rounded-sm bg-accent text-white" data-testid="invite-send">Send invitation</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Consolidation() {
  const [mode, setMode] = useState("statutory");
  const [fromDate, setFromDate] = useState(fyStart());
  const [toDate, setToDate] = useState(today());
  const [entities, setEntities] = useState([]);
  const [selected, setSelected] = useState([]); // multi-select entity IDs
  const [rep, setRep] = useState(null);
  const [invites, setInvites] = useState({ sent: [], received: [] });
  const [loading, setLoading] = useState(false);

  const loadEntities = async () => {
    try {
      const r = await api.get("/consolidation/entities");
      const ents = r.data.entities || [];
      setEntities(ents);
      setSelected(ents.map((e) => e.id)); // default: include all
    } catch (e) { toast.error("Failed to load entities"); }
  };
  const loadInvites = async () => {
    try { const r = await api.get("/consolidation/invites"); setInvites(r.data); } catch (e) { /* ignore */ }
  };
  const run = async () => {
    if (selected.length === 0) return toast.error("Pick at least one entity");
    setLoading(true);
    try {
      const r = await api.get("/consolidation/report", {
        params: { from_date: fromDate, to_date: toDate, mode, entity_ids: selected.join(",") },
      });
      setRep(r.data);
    } catch (e) { toast.error("Consolidation failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadEntities(); loadInvites(); }, []);
  useEffect(() => { if (selected.length) run(); /* eslint-disable-next-line */ }, [mode, selected.length]);

  const toggleEntity = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const respondInvite = async (inv, action, agreedPct) => {
    try {
      if (action === "accept") await api.post(`/consolidation/invites/${inv.id}/accept`, { agreed_ownership_pct: agreedPct || inv.proposed_ownership_pct });
      else await api.post(`/consolidation/invites/${inv.id}/reject`);
      toast.success(`Invite ${action}ed`);
      loadInvites(); loadEntities();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const unlink = async (subId) => {
    if (!window.confirm("Unlink this entity from the group? Historical reports stay viewable, future runs won't include it.")) return;
    try { await api.post(`/consolidation/unlink/${subId}`); toast.success("Unlinked"); loadEntities(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const reparent = async (entityId, newParentId, ownership) => {
    try {
      await api.post("/consolidation/reparent", {
        entity_id: entityId, new_parent_org_id: newParentId, ownership_pct: ownership,
      });
      toast.success("Reparented");
      loadEntities();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reparent failed"); }
  };

  const downloadXlsx = async () => {
    try {
      const r = await api.get("/consolidation/report.xlsx", {
        params: { from_date: fromDate, to_date: toDate, mode, entity_ids: selected.join(",") },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `Consolidation_${mode}_${toDate}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get("/consolidation/report.pdf", {
        params: { from_date: fromDate, to_date: toDate, mode, entity_ids: selected.join(",") },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `Consolidation_${mode}_${toDate}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("PDF download failed"); }
  };

  // Build chart data from per_entity_metrics
  const entityChart = useMemo(() => {
    if (!rep?.per_entity_metrics) return [];
    return Object.entries(rep.per_entity_metrics).map(([id, e]) => ({
      name: e.name?.length > 22 ? e.name.slice(0, 20) + "…" : e.name,
      revenue: e.metrics.revenue,
      ebitda: e.metrics.ebitda,
      eat: e.metrics.eat,
    }));
  }, [rep]);

  // EBITDA-columns table: rows = P&L line items · cols = entities + total
  const ebitdaTable = useMemo(() => {
    if (!rep?.per_entity_metrics) return null;
    const cols = Object.entries(rep.per_entity_metrics);
    const lines = [
      { key: "revenue",       label: "Revenue from operations" },
      { key: "other_income",  label: "Other income" },
      { key: "cogs",          label: "Cost of goods sold",             tone: "negative" },
      { key: "opex",          label: "Operating expenses",             tone: "negative" },
      { key: "gross_profit",  label: "Gross profit",                    tone: "subtotal" },
      { key: "ebitda",        label: "EBITDA",                          tone: "subtotal" },
      { key: "depreciation",  label: "Depreciation & amortisation",    tone: "negative" },
      { key: "ebit",          label: "EBIT",                            tone: "subtotal" },
      { key: "interest",      label: "Interest / finance cost",         tone: "negative" },
      { key: "ebt",           label: "Profit before tax",               tone: "subtotal" },
      { key: "tax",           label: "Tax",                             tone: "negative" },
      { key: "eat",           label: "Profit after tax (EAT)",          tone: "total" },
    ];
    return { cols, lines };
  }, [rep]);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="consolidation-page">
      <PageHeader eyebrow="Group Reporting" title="Consolidation"
        description={mode === "statutory"
          ? "Statutory — Companies Act & AS-21: parent + subsidiaries (≥50%) line-added, intercompany eliminated, minority interest carved out."
          : "MIS — Management view: every marked group entity line-added, intercompany eliminated, no minority carve-out."}
        actions={
          <div className="flex items-center gap-2">
            <InviteDialog onSent={loadInvites} />
            <Button variant="outline" className="rounded-sm h-9 gap-1.5" onClick={downloadXlsx} data-testid="cons-download-xlsx">
              <Download className="w-3.5 h-3.5" /> Excel
            </Button>
            <Button variant="outline" className="rounded-sm h-9 gap-1.5" onClick={downloadPdf} data-testid="cons-download-pdf">
              <Download className="w-3.5 h-3.5" /> PDF
            </Button>
          </div>
        } />

      {/* Pending invites (received) */}
      {invites.received.filter((i) => i.status === "pending").length > 0 && (
        <div className="card-sharp p-5 border-l-4 border-amber-500" data-testid="pending-invites">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-amber-500" />
            <div className="text-sm font-heading">Group invitations awaiting your response</div>
          </div>
          {invites.received.filter((i) => i.status === "pending").map((inv) => (
            <div key={inv.id} className="flex items-center justify-between border-t pt-3 mt-3">
              <div>
                <div className="text-sm"><b>{inv.from_org_name}</b> invited you as a subsidiary at <b>{inv.proposed_ownership_pct}%</b> ownership.</div>
                {inv.notes && <div className="text-xs text-muted-foreground mt-1">"{inv.notes}"</div>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="rounded-sm bg-emerald-600 text-white h-8" onClick={() => respondInvite(inv, "accept")} data-testid={`invite-accept-${inv.id.slice(0,6)}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Accept</Button>
                <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => respondInvite(inv, "reject")} data-testid={`invite-reject-${inv.id.slice(0,6)}`}><XCircle className="w-3.5 h-3.5 mr-1" />Reject</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className="rounded-sm">
            <TabsTrigger value="statutory" className="rounded-sm gap-1.5" data-testid="cons-tab-statutory"><Building2 className="w-3.5 h-3.5" /> Statutory (AS-21)</TabsTrigger>
            <TabsTrigger value="mis" className="rounded-sm gap-1.5" data-testid="cons-tab-mis"><Layers className="w-3.5 h-3.5" /> MIS</TabsTrigger>
          </TabsList>
        </Tabs>
        <Label>From</Label>
        <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-40 rounded-sm h-9" data-testid="cons-from" />
        <Label>To</Label>
        <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-40 rounded-sm h-9" data-testid="cons-to" />
        <Button onClick={run} className="rounded-sm bg-accent text-white" data-testid="cons-run">{loading ? "Loading…" : "Run"}</Button>
      </div>

      {/* Group Tree Visualiser */}
      <div className="card-sharp overflow-hidden">
        <div className="px-4 py-3 border-b text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          Group hierarchy · click a node to include / exclude from this run · {selected.length}/{entities.length} selected
        </div>
        <GroupTree entities={entities} selected={selected} onToggle={toggleEntity} onReparent={reparent} />
        <div className="px-4 py-2 border-t bg-muted/40 flex items-center gap-3">
          <div className="text-xs text-muted-foreground flex-1">Multi-select entities via the tree above or the checkboxes below.</div>
          {entities.filter((e) => e.parent_org_id).map((s) => (
            <button key={s.id} onClick={() => unlink(s.id)} className="text-xs text-muted-foreground hover:text-red-500 inline-flex items-center gap-1" data-testid={`unlink-${s.id.slice(0,6)}`}>
              <Link2Off className="w-3 h-3" /> unlink {s.name.split(" ")[0]}
            </button>
          ))}
        </div>
      </div>

      {/* Entity list with checkboxes */}
      <div className="card-sharp p-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Include in this consolidation run</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {entities.map((e) => (
            <label key={e.id} className="flex items-center gap-2 border rounded-sm px-3 py-2 cursor-pointer hover:bg-muted" data-testid={`entity-check-${e.id.slice(0,8)}`}>
              <Checkbox checked={selected.includes(e.id)} onCheckedChange={() => toggleEntity(e.id)} />
              <div className="min-w-0 flex-1">
                <div className="text-sm truncate">{e.name}</div>
                <div className="text-[10px] font-mono uppercase text-muted-foreground">{e.parent_org_id ? "SUBSIDIARY" : "PARENT"} · {e.ownership_pct}%</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {rep && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">IC Sales elim.</div><div className="metric-value text-2xl mt-2">{fmtINR(rep.eliminations.ic_sales)}</div></div>
            <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">IC Purchases elim.</div><div className="metric-value text-2xl mt-2">{fmtINR(rep.eliminations.ic_purchases)}</div></div>
            <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">IC AR elim.</div><div className="metric-value text-2xl mt-2">{fmtINR(rep.eliminations.ic_ar)}</div></div>
            <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">{mode === "statutory" ? "Minority Interest" : "IC AP elim."}</div><div className="metric-value text-2xl mt-2">{fmtINR(mode === "statutory" ? (rep.minority_interest || 0) : rep.eliminations.ic_ap)}</div></div>
          </div>

          <Tabs defaultValue="ebitda">
            <TabsList className="rounded-sm">
              <TabsTrigger value="ebitda" className="rounded-sm" data-testid="ct-tab-ebitda">Consolidated EBITDA</TabsTrigger>
              <TabsTrigger value="contribution" className="rounded-sm" data-testid="ct-tab-contribution">Entity contribution</TabsTrigger>
              <TabsTrigger value="tb" className="rounded-sm" data-testid="ct-tab-tb">Consolidated Trial Balance</TabsTrigger>
            </TabsList>

            <TabsContent value="ebitda" className="mt-4">
              <div className="card-sharp overflow-x-auto">
                <div className="px-4 py-3 border-b text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Consolidated EBITDA — entities as columns</div>
                {ebitdaTable && (
                  <table className="w-full dense">
                    <thead>
                      <tr>
                        <th className="min-w-[240px]">Line item</th>
                        {ebitdaTable.cols.map(([id, e]) => (
                          <th key={id} className="text-right min-w-[160px]">
                            <div className="text-xs">{e.name}</div>
                            <div className="text-[9px] font-mono uppercase text-muted-foreground">{e.ownership_pct}%</div>
                          </th>
                        ))}
                        <th className="text-right min-w-[160px] bg-blue-50">TOTAL (Consol)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ebitdaTable.lines.map((line) => {
                        const total = ebitdaTable.cols.reduce((s, [_, e]) => s + (e.metrics[line.key] || 0), 0);
                        const cls = line.tone === "subtotal" ? "bg-blue-50/40 font-semibold" :
                                     line.tone === "total" ? "bg-blue-100 font-bold text-blue-900" : "";
                        return (
                          <tr key={line.key} className={cls}>
                            <td>{line.label}</td>
                            {ebitdaTable.cols.map(([id, e]) => (
                              <td key={id} className={`text-right tabular-nums ${line.tone === "negative" ? "text-red-600" : ""}`}>
                                {fmtINR(e.metrics[line.key])}
                              </td>
                            ))}
                            <td className={`text-right tabular-nums ${line.tone === "negative" ? "text-red-600" : ""} bg-blue-50 font-medium`}>{fmtINR(total)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </TabsContent>

            <TabsContent value="contribution" className="mt-4 space-y-6">
              <div className="card-sharp p-5">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Revenue vs EBITDA · per entity</div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={entityChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" fontSize={10} />
                    <YAxis fontSize={10} tickFormatter={(v) => `₹${(v / 10000000).toFixed(1)}Cr`} />
                    <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                    <Legend />
                    <Bar dataKey="revenue" fill="hsl(220 90% 55%)" name="Revenue" />
                    <Bar dataKey="ebitda"  fill="hsl(158 89% 33%)" name="EBITDA" />
                    <Bar dataKey="eat"     fill="hsl(280 76% 55%)" name="EAT" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="tb" className="mt-4">
              <div className="card-sharp overflow-hidden">
                <div className="px-4 py-3 border-b flex items-center justify-between">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Consolidated Trial Balance ({rep.mode.toUpperCase()})</div>
                  <div className="text-xs text-muted-foreground">{rep.rows.length} accounts · Dr {fmtINR(rep.totals.debit)} · Cr {fmtINR(rep.totals.credit)}</div>
                </div>
                <table className="w-full dense">
                  <thead><tr><th>Account</th><th>Type</th><th className="text-right">Debit</th><th className="text-right">Credit</th><th className="text-right">Contributors</th></tr></thead>
                  <tbody>
                    {rep.rows.slice(0, 200).map((r, i) => (
                      <tr key={i}>
                        <td>{r.account_name}</td>
                        <td className="text-xs text-muted-foreground">{r.account_type || "—"}</td>
                        <td className="text-right tabular-nums">{r.debit > 0 ? fmtINR(r.debit) : "—"}</td>
                        <td className="text-right tabular-nums">{r.credit > 0 ? fmtINR(r.credit) : "—"}</td>
                        <td className="text-right text-xs text-muted-foreground">{r.contributions?.length || 0} entities</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Sent invites — status */}
      {invites.sent.length > 0 && (
        <div className="card-sharp p-5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Invitations sent</div>
          <table className="w-full dense">
            <thead><tr><th>Sent to</th><th>GSTIN</th><th>Proposed %</th><th>Status</th><th>When</th></tr></thead>
            <tbody>
              {invites.sent.map((i) => (
                <tr key={i.id}>
                  <td>{i.to_org_name}</td>
                  <td className="font-mono text-xs">{i.to_gstin || "—"}</td>
                  <td>{i.proposed_ownership_pct}%</td>
                  <td>
                    <span className={`text-[10px] px-2 py-0.5 rounded-sm ${
                      i.status === "accepted" ? "bg-emerald-100 text-emerald-700" :
                      i.status === "pending" ? "bg-amber-100 text-amber-700" :
                      "bg-gray-100 text-gray-700"}`}>{i.status.toUpperCase()}</span>
                  </td>
                  <td className="text-xs text-muted-foreground">{new Date(i.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
